/**
 * Field Mapping Utilities
 * 
 * Maps API field keys to React Hook Form field paths.
 * Handles UUID to array index resolution for claim lines.
 */

import type { ClaimLine, ParsedField } from '../types';

import { getFormFieldName } from './fieldNameMapping';

/**
 * Parse an API field key into a structured descriptor
 * 
 * @example
 * parseFieldKey("patient_dob") // { type: 'top-level', field: 'patient_dob' }
 * parseFieldKey("diagnoses.0") // { type: 'array', field: 'diagnoses', index: 0 }
 * parseFieldKey("claim_lines.uuid-123.cpt_code") // { type: 'claim-line', lineId: 'uuid-123', field: 'cpt_code' }
 * parseFieldKey("claim_lines.uuid-123.modifiers.0") // { type: 'claim-line-array', lineId: 'uuid-123', field: 'modifiers', index: 0 }
 */
export function parseFieldKey(fieldKey: string): ParsedField {
  // Handle claim line fields
  if (fieldKey.startsWith('claim_lines.')) {
    const parts = fieldKey.split('.');
    const lineId = parts[1];
    const field = parts[2];
    
    // Check if there's an array index
    if (parts.length === 4 && /^\d+$/.test(parts[3])) {
      return {
        type: 'claim-line-array',
        lineId,
        field,
        index: parseInt(parts[3], 10),
      };
    }
    
    return {
      type: 'claim-line',
      lineId,
      field,
    };
  }
  
  // Handle top-level array fields (e.g., diagnoses.0)
  const parts = fieldKey.split('.');
  if (parts.length === 2 && /^\d+$/.test(parts[1])) {
    return {
      type: 'array',
      field: parts[0],
      index: parseInt(parts[1], 10),
    };
  }
  
  // Top-level field
  return {
    type: 'top-level',
    field: fieldKey,
  };
}

/**
 * Map an API field key to a React Hook Form field path
 * 
 * @param fieldKey - The API field key (e.g., "claim_lines.uuid.cpt_code")
 * @param claimLines - Array of claim lines with IDs for UUID resolution
 * @returns The RHF field path (e.g., "claim_lines[2].cpt_code") or null if not mappable
 */
export function mapToFormField(
  fieldKey: string,
  claimLines?: ClaimLine[]
): string | null {
  const parsed = parseFieldKey(fieldKey);
  
  let result: string | null = null;
  
  switch (parsed.type) {
    case 'top-level':
      // Use the field name mapping for top-level fields
      result = getFormFieldName(parsed.field);
      break;
      
    case 'array': {
      // Special handling for diagnoses array - maps to letter-based fields
      if (parsed.field === 'diagnoses') {
        // The mapping from diagnoses.0 to diagnosisCodes.A is handled in fieldNameMapping
        const mappedField = getFormFieldName(`${parsed.field}.${parsed.index}`);
        result = mappedField;
      } else {
        // Convert dot notation to bracket notation for other arrays
        const baseField = getFormFieldName(parsed.field);
        result = `${baseField}[${parsed.index}]`;
      }
      break;
    }
      
    case 'claim-line': {
      if (!claimLines) {
        return null;
      }
      
      // Find the index of the claim line with this ID
      const lineIndex = claimLines.findIndex(line => line.id === parsed.lineId);
      if (lineIndex === -1) {
        return null;
      }
      
      // Map the field name within the claim line
      const mappedField = getFormFieldName(parsed.field);
      result = `serviceLines[${lineIndex}].${mappedField}`;
      break;
    }
    
    case 'claim-line-array': {
      if (!claimLines) {
        return null;
      }
      
      // Find the index of the claim line with this ID
      const lineIndex = claimLines.findIndex(line => line.id === parsed.lineId);
      if (lineIndex === -1) {
        return null;
      }
      
      // Map the field name within the claim line
      const mappedField = getFormFieldName(parsed.field);
      result = `serviceLines[${lineIndex}].${mappedField}[${parsed.index}]`;
      break;
    }
    
    default:
      return null;
  }
  
  return result;
}

/**
 * Create a mapping of claim line IDs to their array indices
 * Useful for batch processing of annotations
 */
export function createLineIdMap(claimLines: ClaimLine[]): Map<string, number> {
  const map = new Map<string, number>();
  claimLines.forEach((line, index) => {
    if (line.id) {
      map.set(line.id, index);
    }
  });
  return map;
}

/**
 * Get a human-readable label for a field
 * 
 * @example
 * getFieldLabel("patient_dob") // "Patient Date of Birth"
 * getFieldLabel("claim_lines.uuid.cpt_code") // "Service Line - CPT Code"
 */
export function getFieldLabel(fieldKey: string): string {
  // Handle special cases first
  if (fieldKey.startsWith('diagnosisCodes.')) {
    const letter = fieldKey.split('.')[1];
    return `Diagnosis Code ${letter}`;
  }
  
  if (fieldKey.startsWith('diagnoses.')) {
    const parts = fieldKey.split('.');
    if (parts.length === 2 && /^\d+$/.test(parts[1])) {
      const index = parseInt(parts[1], 10);
      const letter = String.fromCharCode(65 + index); // 65 is 'A'
      return `Diagnosis Code ${letter}`;
    }
  }
  
  if (fieldKey.startsWith('serviceLines[')) {
    // Extract the index and field name
    const match = fieldKey.match(/serviceLines\[(\d+)\]\.(.+)/);
    if (match) {
      const lineNum = parseInt(match[1], 10) + 1;
      const field = match[2];
      return `Service Line ${lineNum} - ${field}`;
    }
  }
  
  const parsed = parseFieldKey(fieldKey);
  
  // Map of field names to human-readable labels
  const fieldLabels: Record<string, string> = {
    // Patient fields
    patient_dob: 'Patient Date of Birth',
    patient_sex: 'Patient Sex',
    patient_first_name: 'Patient First Name',
    patient_last_name: 'Patient Last Name',
    patient_middle_initial: 'Patient Middle Initial',
    
    // Provider fields
    provider_npi: 'Provider NPI',
    provider_display_name: 'Provider Name',
    tax_id: 'Tax ID Number',
    tax_id_type: 'Tax ID Type',
    
    // Insurance fields
    insurance_type: 'Insurance Type',
    primary_insured_id_number: 'Insured ID Number',
    primary_insured_first_name: 'Primary Insured First Name',
    primary_insured_last_name: 'Primary Insured Last Name',
    
    // Diagnoses
    diagnoses: 'Diagnosis Code',
    diagnosisCodes: 'Diagnosis Code',
    
    // Claim line fields
    cpt_code: 'CPT Code',
    charge_cents: 'Charge Amount',
    units: 'Units',
    modifiers: 'Modifier',
    diagnosis_pointers: 'Diagnosis Pointer',
    date_from: 'Service Date From',
    date_to: 'Service Date To',
    pos_code: 'Place of Service',
    rendering_provider_npi: 'Rendering Provider NPI',
  };
  
  switch (parsed.type) {
    case 'top-level':
      return fieldLabels[parsed.field] || parsed.field;
      
    case 'array':
      return `${fieldLabels[parsed.field] || parsed.field} #${parsed.index + 1}`;
      
    case 'claim-line':
      return `Service Line - ${fieldLabels[parsed.field] || parsed.field}`;
      
    case 'claim-line-array':
      return `Service Line - ${fieldLabels[parsed.field] || parsed.field} #${parsed.index + 1}`;
      
    default:
      return fieldKey;
  }
}